import React, { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Nav,
  Dropdown,
  Row,
  Col,
  Spinner,
  Accordion,
  Form,
  Alert,
} from "react-bootstrap";
import {
  ArrowLeft,
  User,
  FileText,
  MapPin,
  Calendar,
  MessageSquare,
  FileBarChart,
  Star,
  Phone,
  Mail,
  ChevronDown,
  Plus,
  Filter,
  MoreHorizontal,
  Eye,
  BarChart3,
  Copy,
  Share2,
  Edit3,
  Settings,
  AlertCircle,
  Briefcase, // ← ADD THIS LINE
  X,
} from "lucide-react";

import SummaryTab from "../contactpopup/tabs/SummaryTab.jsx";
import DetailsTab from "../contactpopup/tabs/DetailsTab.jsx";
import CommentsSection from "../../commentSection/CommentSection.jsx"; // Import the reusable component

import JobsTab from "../contactpopup/tabs/JobsTab.jsx";
// Import services
import { getOrganizations, getOrganizationById } from "../../../../services/organizationServices.js";
import { getPlacementOfficers, getPlacementOfficerById } from "../../../../services/PlacementOfficerServices.js";
import { getContactById } from "../../../../services/contactServices.js";

import "./ContactPopup.css";

const TabComponents = {
  summary: SummaryTab,
  details: DetailsTab,
  jobs: JobsTab, // ← ADD THIS LINE
};

const ContactPopupView = ({
  show,
  onHide,
  contact,
  onUpdateContact,
  defaultActiveTab = "summary",
}) => {
  const [activeTab, setActiveTab] = useState(defaultActiveTab);
  const [rightPanelActiveTab, setRightPanelActiveTab] = useState("oneview");
  const [description, setDescription] = useState("");
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [isStarred, setIsStarred] = useState(false);
  const [loadingTab, setLoadingTab] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [officers, setOfficers] = useState([]);
  const [enrichedContact, setEnrichedContact] = useState(null);
  const [detailedContact, setDetailedContact] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [error, setError] = useState(null);
  const fetchState = React.useRef({ organizations: false, officers: false, details: false });

  // Update activeTab when defaultActiveTab prop changes
  useEffect(() => {
    setActiveTab(defaultActiveTab);
  }, [defaultActiveTab, show]);

  // Enrich contact data with organization and user names
  useEffect(() => {
    const baseContact = detailedContact || contact;
    if (baseContact) {
      // 1. Resolve Organization Info
      let organizationInfo = Array.isArray(baseContact.organizationId)
        ? baseContact.organizationId[0]
        : typeof baseContact.organizationId === "object"
        ? baseContact.organizationId
        : null;

      // If not an object, try to find in loaded organizations state
      if (!organizationInfo && typeof baseContact.organizationId === "string") {
        organizationInfo = organizations.find(org => org.id === baseContact.organizationId);
      }

      // 2. Resolve Assigned User Info
      let assignedUserInfo = Array.isArray(baseContact.assignedToId)
        ? baseContact.assignedToId[0]
        : typeof baseContact.assignedToId === "object"
        ? baseContact.assignedToId
        : null;

      // If not an object, try to find in loaded officers state
      if (!assignedUserInfo && typeof baseContact.assignedToId === "string") {
        assignedUserInfo = officers.find(off => off.id === baseContact.assignedToId);
      }

      setEnrichedContact({
        ...baseContact,
        organizationName:
          organizationInfo?.name ||
          (typeof baseContact.organizationId === "string"
            ? baseContact.organizationId
            : "Unknown"),
        assignedToName: assignedUserInfo
          ? `${assignedUserInfo.firstName || ""} ${
               assignedUserInfo.lastName || ""
            }`.trim()
          : typeof baseContact.assignedToId === "string"
          ? baseContact.assignedToId
          : "Unknown",
        assignedToEmail: assignedUserInfo?.email || "",
        // Keep original IDs for editing (extract from objects if needed)
        originalOrganizationId: organizationInfo?.id || baseContact.organizationId,
        originalAssignedToId: assignedUserInfo?.id || baseContact.assignedToId,
      });
    }
  }, [contact, detailedContact, organizations, officers]);

  // Helper to parse IDs
  const parseIds = (field) => {
    if (!field) return [];
    if (Array.isArray(field)) {
      return field.map(item => typeof item === 'object' ? item.id : String(item)).filter(Boolean);
    }
    if (typeof field === 'string') {
      return field.split(',').map(s => s.trim()).filter(Boolean);
    }
    return [];
  };

  // Load organizations for dropdown resolution by specific IDs
  const loadOrganizations = async () => {
    const baseContact = detailedContact || contact;
    if (fetchState.current.organizations) return;
    fetchState.current.organizations = true;
    try {
      const orgIds = parseIds(baseContact?.organizationId);
      if (orgIds.length > 0) {
        const orgPromises = orgIds.map(id => getOrganizationById(id).catch(() => null));
        const results = await Promise.all(orgPromises);
        const validOrgs = results.map(r => {
          const data = r?.data || r;
          // If data is an array (like [{...}]), take the first element
          return Array.isArray(data) ? data[0] : data;
        }).filter(Boolean);
        setOrganizations(validOrgs);
      } else {
        setOrganizations([]);
      }
    } catch (error) {
      console.error("Failed to load organizations by ID:", error);
      fetchState.current.organizations = false;
    }
  };

  // Load officers for assigned user name resolution by specific IDs
  const loadOfficers = async () => {
    const baseContact = detailedContact || contact;
    if (fetchState.current.officers) return;
    fetchState.current.officers = true;
    try {
      const officerIds = parseIds(baseContact?.assignedToId);
      if (officerIds.length > 0) {
        const officerPromises = officerIds.map(id => getPlacementOfficerById(id).catch(() => null));
        const results = await Promise.all(officerPromises);
        const validOfficers = results.map(r => {
          const data = r?.data || r;
          // If data is an array (like [{...}]), take the first element
          return Array.isArray(data) ? data[0] : data;
        }).filter(Boolean);
        setOfficers(validOfficers);
      } else {
        setOfficers([]);
      }
    } catch (error) {
      console.error("Failed to load officers by ID:", error);
      fetchState.current.officers = false;
    }
  };

  // Load full contact details if we only have a stub
  const loadFullContact = async () => {
    if (!contact?.id || fetchState.current.details) return;

    // Only fetch if we're missing key fields (like createdAt or full organization objects)
    if (contact.createdAt && (typeof contact.organizationId === 'object' || Array.isArray(contact.organizationId))) return;

    fetchState.current.details = true;
    setLoadingDetails(true);
    try {
      const response = await getContactById(contact.id);
      const data = response?.data?.[0] || response?.data || response;
      if (data) {
        setDetailedContact(data);
      }
    } catch (error) {
      console.error("Failed to load contact details:", error);
    } finally {
      setLoadingDetails(false);
    }
  };

  const lastContactId = React.useRef(null);

  useEffect(() => {
    if (show && contact) {
      // Reset local state if opening a different contact
      if (lastContactId.current !== contact.id) {
        setOrganizations([]);
        setOfficers([]);
        setDetailedContact(null);
        fetchState.current = { organizations: false, officers: false, details: false };
        lastContactId.current = contact.id;
      }
      
      const baseContact = detailedContact || contact;
      if (!fetchState.current.details) loadFullContact();

      // Check if we need to resolve organizations/officers (reset fetch flag if baseContact has new IDs)
      const currentOrgIds = parseIds(baseContact.organizationId);
      const currentOfficerIds = parseIds(baseContact.assignedToId);

      // 1. Resolve Organization Names
      if (organizations.length === 0) {
        if (Array.isArray(baseContact.organizationId) && baseContact.organizationId.length > 0 && typeof baseContact.organizationId[0] === 'object') {
          setOrganizations(baseContact.organizationId);
          fetchState.current.organizations = true;
        } else if (currentOrgIds.length > 0) {
          // If detailedContact just provided IDs we didn't have before, allow re-fetch
          if (fetchState.current.details && fetchState.current.organizations) {
            fetchState.current.organizations = false;
          }
          loadOrganizations();
        } else {
          fetchState.current.organizations = true;
        }
      }

      // 2. Resolve Officer Names
      if (officers.length === 0) {
        if (Array.isArray(baseContact.assignedToId) && baseContact.assignedToId.length > 0 && typeof baseContact.assignedToId[0] === 'object') {
          setOfficers(baseContact.assignedToId);
          fetchState.current.officers = true;
        } else if (currentOfficerIds.length > 0) {
          // If detailedContact just provided IDs we didn't have before, allow re-fetch
          if (fetchState.current.details && fetchState.current.officers) {
            fetchState.current.officers = false;
          }
          loadOfficers();
        } else {
          fetchState.current.officers = true;
        }
      }
    }
  }, [show, contact, organizations.length, officers.length, detailedContact]);


  // Handle comments error
  const handleCommentsError = (error) => {
    setError(error);
  };

  if (!contact) return null;

  const tabs = [
    { key: "summary", label: "Summary", icon: User },
    { key: "details", label: "Details", icon: FileText },
    { key: "jobs", label: "Jobs", icon: Briefcase }, // ← ADD THIS LINE
  ];

  const visibleTabs = tabs.slice(0, 7);
  const hiddenTabs = tabs.slice(7);
  const TabIcon = ({ icon: Icon, active }) => (
    <Icon
      size={16}
      color={active ? "#ea580c" : "#6c757d"}
      style={{ marginRight: "6px" }}
    />
  );

  const handleCopyContact = () =>
    navigator.clipboard.writeText(`${contact.name}`);
  const handleShareContact = () => {};
  const handleEditContact = () => {};
  const handleEmail = () => (window.location.href = `mailto:${contact.email}`);
  const handleCall = () => (window.location.href = `tel:${contact.phone}`);

  const handleTabSelect = (key) => {
    setLoadingTab(true);
    setTimeout(() => {
      setActiveTab(key);
      setLoadingTab(false);
    }, 500);
  };

  const CurrentTabComponent = TabComponents[activeTab];
  const renderTabContent = () => {
    if (loadingTab) {
      return (
        <div
          style={{
            textAlign: "center",
            padding: "60px 20px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "300px",
          }}
        >
          <Spinner
            animation="border"
            style={{
              color: "#f97316",
              width: "2.5rem",
              height: "2.5rem",
              borderWidth: "3px",
            }}
          />
          <p style={{ marginTop: "16px", color: "#6c757d" }}>Loading...</p>
        </div>
      );
    }
    return (
      <CurrentTabComponent
        contact={enrichedContact || contact}
        onHide={onHide}
        onUpdateContact={onUpdateContact}
        description={description}
        setDescription={setDescription}
        isEditingDescription={isEditingDescription}
        setIsEditingDescription={setIsEditingDescription}
        organizations={organizations}
        users={officers}
      />
    );
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      centered
      className="contact-modal"
      backdrop="static"
    >
      <div className="modal-container" style={{ padding: "20px" }}>
        <Modal.Header className="contact-modal-header" style={{ position: "relative" }}>
          <Button
            variant="link"
            onClick={onHide}
            style={{
              position: "absolute",
              top: "10px",
              right: "15px",
              zIndex: 1060,
              color: "#6c757d",
              textDecoration: "none",
              border: "none",
              background: "transparent",
              padding: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "50%",
              transition: "all 0.2s ease",
            }}
            className="modal-close-x-btn"
            title="Close"
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#f8f9fa";
              e.currentTarget.style.color = "#212529";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";
              e.currentTarget.style.color = "#6c757d";
            }}
          >
            <X size={20} />
          </Button>
          <div className="header-content" style={{ paddingRight: "40px" }}>
            <div className="left-section">
              <Button variant="link" onClick={onHide} className="close-btn">
                <ArrowLeft size={20} />
              </Button>
              <div className="contact-info">
                <div className="avatar">{contact.name?.charAt(0) || "C"}</div>
                <div className="contact-details">
                  <h5 className="contact-name">{contact.name}</h5>
                  <div className="name-icons">
                    {/* <Button
                      variant="link"
                      className="name-icon-btn"
                      onClick={() => setIsStarred(!isStarred)}
                    >
                      <Star size={16} color={isStarred ? '#ffc107' : '#6c757d'} fill={isStarred ? '#ffc107' : 'none'} />
                    </Button>
                    <Button variant="link" className="name-icon-btn" onClick={handleCopyContact}>
                      <Copy size={16} />
                    </Button>
                    <Button variant="link" className="name-icon-btn" onClick={handleShareContact}>
                      <Share2 size={16} />
                    </Button> */}
                  </div>
                </div>
              </div>
            </div>
            <div className="right-section">
              {/* <Button
                variant="outline-warning"
                size="sm"
                className="convert-btn"
              >
                Convert Contact
              </Button> */}
              <div className="action-buttons">
                <Button
                  variant="link"
                  className="name-icon-btn"
                  onClick={() => handleTabSelect("details")}
                >
                  <Edit3 size={16} />
                </Button>
                {/* <Button
                  variant="link"
                  size="sm"
                  className="action-btn"
                  onClick={handleEmail}
                >
                  <Mail size={16} />
                </Button>
                <Button
                  variant="link"
                  size="sm"
                  className="action-btn"
                  onClick={handleCall}
                >
                  <Phone size={16} />
                </Button> */}
                {/* <Button variant="link" size="sm" className="action-btn">
                  <MoreHorizontal size={16} />
                </Button> */}
              </div>
            </div>
          </div>
        </Modal.Header>

        {/* Navigation Tabs */}
        <div className="tabs-container">
          <Nav variant="tabs" className="custom-tabs">
            {visibleTabs.map((tab) => (
              <Nav.Item key={tab.key}>
                <Nav.Link
                  active={activeTab === tab.key}
                  onClick={() => handleTabSelect(tab.key)}
                  className="custom-tab-link"
                >
                  <TabIcon icon={tab.icon} active={activeTab === tab.key} />
                  {tab.label}
                </Nav.Link>
              </Nav.Item>
            ))}
            {hiddenTabs.length > 0 && (
              <Nav.Item className="more-tabs-item">
                <Dropdown>
                  <Dropdown.Toggle as={Nav.Link} className="more-tabs-link">
                    <span className="tab-count">{hiddenTabs.length} More</span>
                    <ChevronDown size={12} className="more-dropdown-icon" />
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    {hiddenTabs.map((tab) => (
                      <Dropdown.Item
                        key={tab.key}
                        onClick={() => handleTabSelect(tab.key)}
                        className={activeTab === tab.key ? "active" : ""}
                      >
                        <TabIcon
                          icon={tab.icon}
                          active={activeTab === tab.key}
                        />
                        {tab.label}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
              </Nav.Item>
            )}
            {/* <Nav.Item className="settings-tab-item">
              <Nav.Link className="settings-tab-link">
                <Settings size={16} />
              </Nav.Link>
            </Nav.Item> */}
          </Nav>
        </div>

        <Modal.Body className="contact-modal-body">
          {/* Error Alert */}
          {error && (
            <Alert
              variant="danger"
              className="mb-4"
              dismissible
              onClose={() => setError(null)}
            >
              <AlertCircle size={16} className="me-2" />
              {error}
            </Alert>
          )}

          {/* Summary Tab: Two-column layout with right panel */}
          {activeTab === "summary" && (
            <Row className="main-content">
              <Col lg={12} className="left-panel">
                {renderTabContent()}

                {/* HR Contact Comments Section */}
                {contact.id && (
                  <CommentsSection
                    entityId={contact.id} // Pass the specific contact ID
                    type="contact" // Specify this is for HR contacts
                    title={`Discussion about ${contact.name}`}
                    users={officers}
                    onError={handleCommentsError}
                    showCollapse={true}
                    defaultExpanded={true}
                    maxHeight="500px"
                    className="contact-comments-section"
                  />
                )}
              </Col>
            </Row>
          )}

          {/* All other tabs: Full width, no right panel */}
          {activeTab !== "summary" && (
            <div className="full-width-content">{renderTabContent()}</div>
          )}
        </Modal.Body>
      </div>

      <style>{`
        .contact-modal-body {
          padding: 0;
        }

        .main-content {
          margin: 0;
        }

        .left-panel {
          padding: 20px;
          background: white;
        }

        .full-width-content {
          padding: 20px;
          width: 100%;
        }

        .contact-modal-header {
          border-bottom: 1px solid #e9ecef;
          padding: 16px 20px;
          background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
        }

        .header-content {
          display: flex;
          justify-content: space-between;
          align-items: center;
          width: 100%;
        }

        .left-section {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .close-btn {
          padding: 8px;
          color: #6c757d;
          text-decoration: none;
          border-radius: 50%;
          transition: all 0.2s ease;
        }

        .close-btn:hover {
          background: #f8f9fa;
          color: #495057;
        }

        .contact-info {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .avatar {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          background: linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: bold;
          font-size: 1.2rem;
          text-transform: uppercase;
        }

        .contact-details {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .contact-name {
          margin: 0;
          font-size: 1.25rem;
          font-weight: 600;
          color: #212529;
        }

        .name-icons {
          display: flex;
          gap: 4px;
        }

        .name-icon-btn {
          padding: 4px;
          color: #6c757d;
          text-decoration: none;
          border-radius: 4px;
          transition: all 0.2s ease;
        }

        .name-icon-btn:hover {
          background: #f8f9fa;
          color: #495057;
        }

        .right-section {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .convert-btn {
          border-color: #ffc107;
          color: #ffc107;
          font-weight: 500;
          font-size: 0.875rem;
          padding: 6px 16px;
        }

        .convert-btn:hover {
          background: #ffc107;
          border-color: #ffc107;
          color: #212529;
        }

        .action-buttons {
          display: flex;
          gap: 4px;
        }

        .action-btn {
          width: 36px;
          height: 36px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #6c757d;
          text-decoration: none;
          border-radius: 50%;
          transition: all 0.2s ease;
        }

        .action-btn:hover {
          background: #f8f9fa;
          color: #495057;
        }

        .tabs-container {
          border-bottom: 1px solid #e9ecef;
          background: white;
        }

        .custom-tabs {
          border-bottom: none;
          padding: 0 20px;
        }

        .custom-tab-link {
          color: #6c757d;
          border: none;
          padding: 12px 16px;
          font-weight: 500;
          border-radius: 0;
          transition: all 0.2s ease;
        }

        .custom-tab-link:hover {
          color: #495057;
          background: #f8f9fa;
          border-color: transparent;
        }

        .custom-tab-link.active {
          color: #ea580c;
          background: white;
          border-bottom: 2px solid #ea580c;
        }

        .more-tabs-item .more-tabs-link {
          color: #6c757d;
          padding: 12px 16px;
          font-weight: 500;
          text-decoration: none;
        }

        .more-tabs-link:hover {
          color: #495057;
          background: #f8f9fa;
        }

        .tab-count {
          font-size: 0.875rem;
        }

        .more-dropdown-icon {
          margin-left: 4px;
        }

        .settings-tab-item .settings-tab-link {
          color: #6c757d;
          padding: 12px 16px;
          text-decoration: none;
          transition: all 0.2s ease;
        }

        .settings-tab-link:hover {
          color: #495057;
          background: #f8f9fa;
        }

        /* Comments Section Specific Styling for Contacts */
        .contact-comments-section {
          border: 2px solid #ffe8cc;
          background: linear-gradient(135deg, #fffbf5 0%, #fff8f0 100%);
          margin-top: 30px;
        }

        .debug-info {
          border-left: 3px solid #ffc107;
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          .header-content {
            flex-direction: column;
            gap: 16px;
            align-items: flex-start;
          }

          .right-section {
            align-self: flex-end;
          }

          .custom-tabs {
            padding: 0 10px;
          }

          .left-panel {
            padding: 15px;
          }
        }
      `}</style>
    </Modal>
  );
};

export default ContactPopupView;
